import { useState } from "react";
import { toast } from "sonner";
import { Sparkles, Loader2, Copy, Check, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { useLang } from "@/context/LanguageContext";
import { generateAdvisor } from "@/lib/api";
import SafeMarkdown from "@/components/SafeMarkdown";

export default function AdvisorPanel({ reclassified, ratios, report, onReport, includeInPdf, onIncludeInPdfChange }) {
  const { t, lang } = useLang();
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const run = async () => {
    setLoading(true);
    try {
      const res = await generateAdvisor(reclassified, ratios, lang);
      onReport?.(res.report || "");
    } catch (e) {
      toast.error(t("advisor_error"));
    } finally {
      setLoading(false);
    }
  };

  const copy = async () => {
    if (!report) return;
    await navigator.clipboard.writeText(report);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white" data-testid="advisor-panel">
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-slate-100 px-5 py-4">
        <div className="flex items-start gap-3">
          <div className="grid h-9 w-9 place-items-center rounded-md bg-blue-50 text-blue-700">
            <Sparkles className="h-4 w-4" strokeWidth={2} />
          </div>
          <div>
            <div className="font-heading text-sm font-semibold text-slate-900">{t("advisor_title")}</div>
            <div className="text-xs text-slate-500">{t("advisor_subtitle")}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {report && (
            <Button
              variant="outline"
              size="sm"
              onClick={copy}
              data-testid="advisor-copy-btn"
              className="rounded-full border-slate-300"
            >
              {copied ? <Check className="mr-1.5 h-3.5 w-3.5 text-green-600" /> : <Copy className="mr-1.5 h-3.5 w-3.5" />}
              {copied ? t("advisor_copied") : t("advisor_copy")}
            </Button>
          )}
          <Button
            size="sm"
            onClick={run}
            disabled={loading}
            data-testid="advisor-generate-btn"
            className="rounded-full bg-blue-700 text-white hover:bg-blue-800"
          >
            {loading ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Sparkles className="mr-1.5 h-3.5 w-3.5" strokeWidth={2} />}
            {report ? t("advisor_regenerate") : t("advisor_generate")}
          </Button>
        </div>
      </div>
      <div className="p-5">
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <Loader2 className="h-4 w-4 animate-spin text-blue-700" />
            {t("advisor_generating")}
          </div>
        ) : report ? (
          <>
            <SafeMarkdown text={report} testId="advisor-report" />
            <div className="mt-5 flex items-center justify-between rounded-lg border border-blue-100 bg-blue-50/50 px-4 py-3">
              <div className="flex items-start gap-3">
                <div className="grid h-8 w-8 shrink-0 place-items-center rounded-md bg-white text-blue-700 ring-1 ring-blue-200">
                  <FileText className="h-4 w-4" strokeWidth={2} />
                </div>
                <div>
                  <Label htmlFor="advisor-include-pdf-switch" className="cursor-pointer text-sm font-medium text-slate-900">
                    {t("advisor_include_pdf")}
                  </Label>
                  <div className="text-xs text-slate-500">{t("advisor_included_hint")}</div>
                </div>
              </div>
              <Switch
                id="advisor-include-pdf-switch"
                data-testid="advisor-include-pdf-switch"
                checked={!!includeInPdf}
                onCheckedChange={(v) => onIncludeInPdfChange?.(v)}
              />
            </div>
          </>
        ) : (
          <div className="text-sm text-slate-500">{t("advisor_empty")}</div>
        )}
      </div>
    </div>
  );
}
